const EMBED_BASE = "https://maps.google.com/maps?output=embed";

interface Coordinates {
  lat: number;
  lng: number;
}

const extractCoords = (rawUrl: string): Coordinates | null => {
  try {
    const url = new URL(rawUrl);
    const href = url.href;

    // Exact pin location embedded in place links:
    // ...!8m2!3d<lat>!4d<lng>
    const exact = href.match(/!8m2!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/);
    if (exact) return { lat: Number(exact[1]), lng: Number(exact[2]) };

    // Viewport center from share/place links: .../@lat,lng,17z
    const center = url.pathname.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
    if (center) return { lat: Number(center[1]), lng: Number(center[2]) };

    // Legacy direct params: ?ll=lat,lng or ?q=lat,lng
    const ll = url.searchParams.get("ll");
    if (ll) {
      const [lat, lng] = ll.split(",").map(Number);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
    }
    const q = url.searchParams.get("q");
    if (q) {
      const [lat, lng] = q.split(",").map(Number);
      if (q.includes(",") && !Number.isNaN(lat) && !Number.isNaN(lng)) {
        return { lat, lng };
      }
    }
  } catch {
    return null;
  }
  return null;
};

const embedFromCoords = ({ lat, lng }: Coordinates): string =>
  `${EMBED_BASE}&q=${lat},${lng}&z=17`;

const embedFromQuery = (query: string): string =>
  `${EMBED_BASE}&q=${encodeURIComponent(query)}`;

/**
 * Build an embeddable URL from a Google Maps URL that already carries
 * coordinates (place/share links, ?q=lat,lng, ?ll=…). Short links fall back to
 * a query-based embed that Google cannot always center exactly.
 */
export const toGoogleMapEmbedUrl = (
  googleMapsUrl: string | null | undefined,
): string | null => {
  if (!googleMapsUrl) return null;
  const coords = extractCoords(googleMapsUrl);
  return coords ? embedFromCoords(coords) : embedFromQuery(googleMapsUrl);
};

/**
 * Best-effort resolution of short links (maps.app.goo.gl/…). Browsers block
 * reading the redirect target unless the shortener sends CORS headers, so this
 * usually falls back to the query-based embed. The reliable fix is storing a
 * coordinate-bearing URL (e.g. https://maps.google.com/maps?q=lat,lng) in the
 * backend.
 */
export const resolveGoogleMapEmbedUrl = async (
  googleMapsUrl: string | null | undefined,
): Promise<string | null> => {
  if (!googleMapsUrl) return null;
  if (extractCoords(googleMapsUrl)) return toGoogleMapEmbedUrl(googleMapsUrl);

  try {
    const res = await fetch(googleMapsUrl, { redirect: "follow" });
    if (res.ok && res.url) {
      const coords = extractCoords(res.url);
      if (coords) return embedFromCoords(coords);
      return embedFromQuery(res.url);
    }
  } catch {
    // CORS blocked; fall through to the query-based embed.
  }
  return embedFromQuery(googleMapsUrl);
};